import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Input, Collapse, Form, InputNumber, notification, Modal, Spin } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Factory, Save, Loader2, Search } from 'lucide-react';
import ProductionJointsModal from './ProductionJointsModal';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  formatPlannedCoatingScopeDisplay,
  formatPlannedWorkChipParts,
  getPlannedCoatingQuantity,
  logMainlinePlannedCoatingDebug,
} from '../../utils/plannedWorkItems';
import './TechnicianLogSection.css';
import './ProductionJointsSection.css';
import '../DPR/DPRSettingsPipelinesTab.css';
import {
    fetchProductionLog,
    updateProductionLog,
    upsertProductionItem,
    deletePipeGroup,
    updateDsrLogSections,
    deleteProductionJoint,
    updatePipeGroup,
    updatePipeData,
} from '../../redux/dsr/action';

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

const getPipeGroupDisplayName = (group) =>
  String(group?.name || group?.pipeGroup || 'Unnamed Group').trim();

const getMainlineDisplayTitle = (mainline) =>
  mainline?.pipelineSection ||
  mainline?.pipeline_section ||
  mainline?.name ||
  (mainline?.pipeStringId != null && mainline?.pipeStringId !== ''
    ? `Mainline ${mainline.pipeStringId}`
    : '');

function PlannedWorkMetricChip({ workType, quantity }) {
  if (quantity == null || !(quantity > 0)) return null;
  const { label, value } = formatPlannedWorkChipParts(workType, quantity);
  return (
    <span
      className="dpr-settings-pipelines-metric-chip dpr-settings-pipelines-metric-chip--planned modern-dsr-pj-planned-chip"
      role="listitem"
    >
      <span className="dpr-settings-pipelines-metric-label">{label}</span>
      <span className="dpr-settings-pipelines-metric-value">{value}</span>
    </span>
  );
}

function PlannedCoatingChip({ mainline }) {
  const quantity = mainline ? getPlannedCoatingQuantity(mainline) : null;
  return <PlannedWorkMetricChip workType="Coating" quantity={quantity} />;
}

function findMainlineByPipelineSection(sowDetails, sectionName) {
  const key = normalizeKey(sectionName);
  if (!key) return null;
  return (
    sowDetails.find((s) => {
      const section = String(s.pipelineSection ?? s.pipeline_section ?? '').trim();
      return normalizeKey(section) === key;
    }) || null
  );
}

/** Match pipe group → Details mainline (coating qty). UI title often = pipeDescription, not pipeGroup. */
function resolveMainlineForPipeGroup(group, sowDetails, selectedMainline) {
  const groupName = String(group?.name || group?.pipeGroup || '').trim();
  const joints = group?.production_joint || group?.pipeStrings || [];

  if (!Array.isArray(sowDetails) || !sowDetails.length) {
    return {
      mainline: selectedMainline,
      matchedBy: selectedMainline ? 'selectedMainline (fallback)' : 'none',
    };
  }

  if (groupName) {
    const byGroupName = findMainlineByPipelineSection(sowDetails, groupName);
    if (byGroupName) {
      return { mainline: byGroupName, matchedBy: 'pipeGroup → pipelineSection (exact)' };
    }
  }

  const descriptionCandidates = joints
    .map((j) => j?.pipeDescription ?? j?.pipe_description)
    .filter((v) => v != null && String(v).trim() !== '');
  for (const desc of descriptionCandidates) {
    const byDescription = findMainlineByPipelineSection(sowDetails, desc);
    if (byDescription) {
      return { mainline: byDescription, matchedBy: 'pipeDescription → pipelineSection (exact)' };
    }
  }

  for (const joint of joints) {
    const jointPipeStringId = joint?.pipeStringID ?? joint?.pipeStringId ?? joint?.pipe_string_id;
    if (jointPipeStringId == null || String(jointPipeStringId).trim() === '') continue;
    const pipeKey = normalizeKey(jointPipeStringId);
    const byPipeString = sowDetails.find((s) => {
      const ps = s.pipeStringId ?? s.pipe_string_id ?? s.pipeStringID;
      return ps != null && normalizeKey(ps) === pipeKey;
    });
    if (byPipeString) {
      return { mainline: byPipeString, matchedBy: 'pipeStringId' };
    }
  }

  const selectedKey = normalizeKey(
    selectedMainline?.pipelineSection ?? selectedMainline?.pipeline_section
  );
  if (selectedMainline?.id && selectedKey) {
    const groupOrDescMatchesSelected = [groupName, ...descriptionCandidates].some(
      (label) => normalizeKey(label) === selectedKey
    );
    if (groupOrDescMatchesSelected) {
      return { mainline: selectedMainline, matchedBy: 'selectedMainline (same section label)' };
    }
  }

  const jointSowId = joints[0]?.sowId ?? joints[0]?.sowID;
  if (jointSowId != null && jointSowId !== '') {
    const byJointSow = sowDetails.find(
      (s) => String(s.id) === String(jointSowId) || String(s.sowId) === String(jointSowId)
    );
    if (byJointSow) {
      return { mainline: byJointSow, matchedBy: 'joint.sowId' };
    }
  }

  return {
    mainline: selectedMainline,
    matchedBy: selectedMainline ? 'selectedMainline (fallback)' : 'none',
  };
}

function getGroupJoints(group) {
  return group?.production_joint || group?.pipeStrings || [];
}

function isProductionJointRecord(item) {
  return (
    item?.id != null &&
    (item.pipeDescription != null || item.pipeStringID != null) &&
    !Array.isArray(item?.production_joint)
  );
}

function normalizeProductionJoint(joint) {
  return {
    ...joint,
    firstJointValue: joint.firstJointValue || 0,
    lastJointValue: joint.lastJointValue || 0,
    repair: joint.repair || 0,
    accepted: joint.accepted || 0,
    rejected: joint.rejected || 0,
    pipeDescription: joint.pipeDescription || '',
    factoryCoating: joint.factoryCoating || '',
    fjcType: joint.fjcType || '',
    fjcThicknessValue: joint.fjcThicknessValue || 0,
    fjcThicknessUnit: joint.fjcThicknessUnit || '',
    pipeStringID: joint.pipeStringID || '',
    scope: joint.scope || 0,
  };
}

/** Flatten API log into one row per mainline joint (ignore pipe-group nesting). */
function flattenProductionLogToJoints(logData, shiftNum) {
  if (!logData) return [];
  const items = Array.isArray(logData) ? logData : [logData];
  const rawJoints = [];

  items.forEach((item) => {
    if (isProductionJointRecord(item)) {
      rawJoints.push(item);
      return;
    }
    getGroupJoints(item).forEach((joint) => rawJoints.push(joint));
  });

  return getEditableJoints(
    rawJoints.map(normalizeProductionJoint),
    shiftNum
  );
}

/** Resolve mainline for a single production joint row. */
function resolveMainlineForJoint(joint, sowDetails, selectedMainline) {
  if (joint?.sowId != null && Array.isArray(sowDetails) && sowDetails.length) {
    const bySow = sowDetails.find(
      (s) => String(s.id) === String(joint.sowId) || String(s.sowId) === String(joint.sowId)
    );
    if (bySow) return bySow;
  }

  const pseudoGroup = {
    name: joint?.pipeDescription,
    pipeGroup: joint?.pipeDescription,
    pipeStrings: [joint],
    production_joint: [joint],
  };
  return resolveMainlineForPipeGroup(pseudoGroup, sowDetails, selectedMainline).mainline;
}

function getJointMainlineName(joint, sowDetails, selectedMainline) {
  const mainline = resolveMainlineForJoint(joint, sowDetails, selectedMainline);
  return (
    getMainlineDisplayTitle(mainline) ||
    String(joint?.pipeDescription || '').trim() ||
    String(joint?.pipeStringID || '').trim()
  );
}

function compareMainlineNames(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function isJointForCurrentShift(joint, shiftNum) {
  if (joint?.shift === null || joint?.shift === undefined || joint?.shift === '') return true;
  return Number(joint.shift) === Number(shiftNum);
}

/** Joints editable in the form — must have an id; do not require dsrLogId (often null on first load). */
function getEditableJoints(joints, shiftNum) {
  return (joints || []).filter(
    (joint) => joint?.id != null && isJointForCurrentShift(joint, shiftNum)
  );
}

function isNewStatusJoint(joint) {
  return String(joint?.production_status ?? '').toLowerCase() === 'new';
}

const { Text } = Typography;
const { Panel } = Collapse;
const { confirm } = Modal;

const PipeStringRow = ({ pipeString, onChange, disabled, coatingMainline }) => {
    const totalJoints = ((pipeString.lastJointValue || 0) - (pipeString.firstJointValue || 0) + 1);
    const isError = totalJoints > 0 && ((pipeString.repair || 0) + (pipeString.accepted || 0) + (pipeString.rejected || 0) !== totalJoints);

    const renderHeader = () => (
        <div className="modern-dsr-pj-legacy-header-bar">
            <div className="modern-dsr-pj-block-head-left">
                <Text strong>{pipeString.pipeDescription || 'Unnamed Pipe String'}</Text>
            </div>
            <div className="modern-dsr-pj-meta" style={{ alignItems: 'center' }}>
                <PlannedCoatingChip mainline={coatingMainline} />
                <span>
                    Pipe String ID <strong>{pipeString.pipeStringID || 'N/A'}</strong>
                </span>
            </div>
        </div>
    );

    return (
        <Collapse ghost className="modern-dsr-pj-legacy-row-collapse">
            <Panel header={renderHeader()} key={pipeString.id}>
                <div className="modern-dsr-pj-legacy-panel-body">
                    <div className="modern-dsr-pj-fields">
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="First Joint">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.firstJointValue || 0}
                                    onChange={(value) => onChange(pipeString.id, 'firstJointValue', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Last Joint">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.lastJointValue || 0}
                                    onChange={(value) => onChange(pipeString.id, 'lastJointValue', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Repairs">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.repair || 0}
                                    onChange={(value) => onChange(pipeString.id, 'repair', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Accepted">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.accepted || 0}
                                    onChange={(value) => onChange(pipeString.id, 'accepted', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                        <div className="modern-dsr-pj-field">
                            <Form.Item label="Rejected">
                                <InputNumber
                                    className="modern-dsr-tech-inputnum"
                                    min={0}
                                    defaultValue={pipeString.rejected || 0}
                                    onChange={(value) => onChange(pipeString.id, 'rejected', value)}
                                    disabled={disabled}
                                />
                            </Form.Item>
                        </div>
                    </div>
                    {isError && (
                        <span className="modern-dsr-pj-error">
                            The sum of Repairs, Accepted, and Rejected must be equal to Total Joints ({totalJoints})
                        </span>
                    )}
                </div>
            </Panel>
        </Collapse>
    );
};

const NewPipeStringRow = ({ pipeString, onChange, coatingMainline, disabled }) => {
  const totalJoints = (pipeString.lastJointValue || 0) - (pipeString.firstJointValue || 0) + 1;
  const isError =
    totalJoints > 0 &&
    (pipeString.repair || 0) + (pipeString.accepted || 0) + (pipeString.rejected || 0) !==
      totalJoints;

  return (
    <div className="modern-dsr-pj-block">
      <div className="modern-dsr-pj-block-head">
        <div className="modern-dsr-pj-block-head-left">
          <h4 className="modern-dsr-pj-pipe-name">
            {pipeString.pipeDescription || 'Unnamed Pipe String'}
          </h4>
          <div className="modern-dsr-pj-meta">
            <PlannedCoatingChip mainline={coatingMainline} />
            <span>
              Cumulative Till Date: <strong>{pipeString.cumulative || '0'}</strong>
            </span>
            <span>
              Pipe String ID: <strong>{pipeString.pipeStringID || 'N/A'}</strong>
            </span>
          </div>
        </div>
      </div>
      <div className="modern-dsr-pj-fields">
        <div className="modern-dsr-pj-field">
          <Form.Item label="First Joint">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.firstJointValue || 0}
              onChange={(value) => {
                onChange(pipeString.id, 'firstJointValue', value);
              }}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Last Joint">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.lastJointValue || 0}
              onChange={(value) => {
                onChange(pipeString.id, 'lastJointValue', value);
              }}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Repairs">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.repair || 0}
              onChange={(value) => onChange(pipeString.id, 'repair', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Accepted">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.accepted || 0}
              onChange={(value) => onChange(pipeString.id, 'accepted', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
        <div className="modern-dsr-pj-field">
          <Form.Item label="Rejected">
            <InputNumber
              className="modern-dsr-tech-inputnum"
              min={0}
              defaultValue={pipeString.rejected || 0}
              onChange={(value) => onChange(pipeString.id, 'rejected', value)}
              disabled={disabled}
            />
          </Form.Item>
        </div>
      </div>

      {isError && (
        <span className="modern-dsr-pj-error">
          The sum of Repairs, Accepted, and Rejected must equal Total Joints ({totalJoints})
        </span>
      )}
    </div>
  );
};


const ProductionJointsSection = ({
    shift, selectedDate, onComplete, onCancel, selectedProject, selectedMainline, sowDetails = [], currentDsrLog,
    // Redux
    currentProductionLog, isUpdatingProductionLog, isProductionLogLoading,
    onFetchProductionLog, onUpdateProductionLog, onUpsertProductionItem, onDeletePipeGroup, onUpdateDsrLogSections, onDeleteProductionJoint, onUpdatePipeGroup, onUpdatePipeData,
    isSkipped, isSubmitted, isShiftSkipped = false, onSkipChange
}) => {
    const [skipped, setSkipped] = useState(false);
    const [comments, setComments] = useState('');
    const [mainlineJoints, setMainlineJoints] = useState([]);
    const [mainlineSearch, setMainlineSearch] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalConfig, setModalConfig] = useState({ mode: '', data: {}, context: {} });
    const [isDirty, setIsDirty] = useState(false);

    const getShiftNumber = (shiftStr) => (shiftStr === 'day' ? 0 : 1);
    const shiftKey = shift === 'day' ? 'isProductionJointSkippedDayShift' : 'isProductionJointSkippedNightShift';
    const displayDate = moment(selectedDate).format('DD MMM YYYY');

    const getRefreshParams = () => ({
        projectId: selectedProject.id,
        logDate: moment(selectedDate).format('YYYY-MM-DD'),
        userEmail: JSON.parse(localStorage.getItem('user')).email,
        shift: getShiftNumber(shift),
        sowId: selectedMainline.id
    });


    const fetchLogData = useCallback(() => {
        if (selectedProject && selectedMainline && selectedDate && currentDsrLog) {
            onFetchProductionLog({
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id,
            });
        }
    }, [selectedProject, selectedMainline, selectedDate, currentDsrLog, shift, onFetchProductionLog]);

    useEffect(() => {
        fetchLogData();
    }, [fetchLogData]);

    // Update skip state from props
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped]);

    // Reset dirty state when date changes (new data should be loaded)
    useEffect(() => {
        setIsDirty(false);
    }, [selectedDate]);

    // Reset skip state when section or shift changes
    useEffect(() => {
        setSkipped(isSkipped || false);
    }, [isSkipped, shift, selectedDate]);

    // Clear local state when component unmounts
    useEffect(() => {
        return () => {
            setMainlineJoints([]);
            setComments('');
            setSkipped(false);
            setIsDirty(false);
            setIsModalVisible(false);
        };
    }, []);

    useEffect(() => {
        if (currentProductionLog) {
            const joints = flattenProductionLogToJoints(
                currentProductionLog,
                getShiftNumber(shift)
            );
            setMainlineJoints(joints);

            const logEntries = Array.isArray(currentProductionLog)
                ? currentProductionLog
                : [currentProductionLog];
            const firstEntry = logEntries[0] || {};
            setComments(
                shift === 'day'
                    ? (firstEntry.dayShiftComments || '')
                    : (firstEntry.nightShiftComments || '')
            );
            setIsDirty(false);
        } else {
            setMainlineJoints([]);
            setComments('');
        }
    }, [currentProductionLog, shift]);

    const showModal = (mode, data = {}, context = {}) => {
        console.log('showModal called with:', { mode, data, context });

        let initialData;
        if (mode.includes('Group')) {
            initialData = {
                ...data,
                name: data.pipeGroup || '',
                scope: data.scope || 0
            };
        } else if (mode.includes('PipeData')) {
            // Map API response fields to modal form fields with proper null handling
            initialData = {
                ...data,
                // Map fjcThicknessValue to fjcThickness for the form
                fjcThickness: data.fjcThicknessValue || 0,
                // Map fjcThicknessUnit to unit for the form
                unit: data.fjcThicknessUnit || '',
                // Map pipeStringID to pipeStringId for the form
                pipeStringId: data.pipeStringID || '',
                // Keep other fields as they are with null handling
                pipeDescription: data.pipeDescription || '',
                factoryCoating: data.factoryCoating || '',
                fjcType: data.fjcType || '',
                scope: data.scope || 0,
                // Ensure other required fields have defaults
                firstJointValue: data.firstJointValue || 0,
                lastJointValue: data.lastJointValue || 0,
                repair: data.repair || 0,
                accepted: data.accepted || 0,
                rejected: data.rejected || 0
            };
        } else {
            initialData = data;
        }
        setModalConfig({ mode, data: initialData, context });
        setIsModalVisible(true);
    };

    const handleCancel = () => setIsModalVisible(false);

    const handleOk = (values) => {
        const { mode, data, context } = modalConfig;
        let payload;
        const commonPayload = {
            projectId: selectedProject.id,
            dsrDate: moment(selectedDate).format('YYYY-MM-DD'),
            shift: getShiftNumber(shift),
        };

        if (mode === 'addGroup') {
            payload = {
                ...commonPayload,
                pipeGroup: values.name || '',
                scope: values.scope || 0,
                isGroup: true
            };

            console.log('Adding group with payload:', { productionJoint: [payload] });
            console.log('Full payload structure:', {
                productionJoint: [payload],
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });

            onUpsertProductionItem({
                productionJoint: [payload],
            // Pass params to refresh data after upsert
                ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
            });
        } else if (mode === 'editGroup') {
            // Use the new updatePipeGroup action for editing pipe groups
            console.log('Editing group with ID:', data.id, 'Group data:', data);

            onUpdatePipeGroup({
                id: data.id,
                pipeGroupData: {
                    scope: values.scope || 0,
                    pipeGroup: values.name || ''
                },
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });
        } else if (mode === 'addPipeData') {
            // Map form values to API format for new pipe data
            const apiPayload = {
                ...commonPayload,
                parentId: context.groupId,
                isChild: true,
                // Map form fields to API fields with null handling
                pipeDescription: values.pipeDescription || '',
                factoryCoating: values.factoryCoating || '',
                fjcType: values.fjcType || '',
                fjcThicknessValue: values.fjcThickness || 0,
                fjcThicknessUnit: values.unit || '',
                scope: values.scope || 0,
                pipeStringID: values.pipeStringId || '',
                // Add default values for joint counts
                firstJointValue: values.firstJointValue || 0,
                lastJointValue: values.lastJointValue || 0,
                repair: values.repair || 0,
                accepted: values.accepted || 0,
                rejected: values.rejected || 0
            };

            console.log('Adding pipe data with payload:', { productionJoint: [apiPayload] });
            console.log('Full pipe data payload structure:', {
                productionJoint: [apiPayload],
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });

            onUpsertProductionItem({
                productionJoint: [apiPayload],
            // Pass params to refresh data after upsert
                ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
            });
        } else if (mode === 'editPipeData') {
            // Use the new updatePipeData action for editing pipe data
            if (!context.groupId) {
                notification.error({ message: 'Group ID is required for editing pipe data.' });
                return;
            }

            const pipeData = {
                id: data.id,
                pipeDescription: values.pipeDescription || '',
                factoryCoating: values.factoryCoating || '',
                fjcType: values.fjcType || '',
                fjcThicknessValue: values.fjcThickness || 0,
                fjcThicknessUnit: values.unit || '',
                scope: values.scope || 0,
                parentId: context.groupId, // Use groupId from context instead of data.parentId
                pipeStringID: values.pipeStringId || '',
                projectId: selectedProject.id,
                // Add joint count fields
                firstJointValue: values.firstJointValue || 0,
                lastJointValue: values.lastJointValue || 0,
                repair: values.repair || 0,
                accepted: values.accepted || 0,
                rejected: values.rejected || 0
            };

            console.log('Updating pipe data with:', pipeData);

            onUpdatePipeData({
                id: data.id,
                pipeData,
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id
            });
        } else if (mode === 'deletePipeData') {
            // Handle delete pipe data
            onDeleteProductionJoint({
                id: data.id,
                ...getRefreshParams(),
            });
        }

        setIsModalVisible(false);
    };

    const handleDeleteGroup = (groupId) => {
        if (!groupId) {
            notification.error({ message: 'Cannot delete group without valid ID.' });
            return;
        }

        console.log('Deleting group with ID:', groupId);

        confirm({
            title: 'Are you sure you want to delete this pipe group?',
            icon: <ExclamationCircleOutlined />,
            content: 'This will delete the group and all its sub-joints. This action cannot be undone.',
            onOk() {
                onDeletePipeGroup({
                    id: groupId,
        // Pass params to refresh data after delete
                    ...{ projectId: selectedProject.id, logDate: moment(selectedDate).format('YYYY-MM-DD'), userEmail: JSON.parse(localStorage.getItem('user')).email, shift: getShiftNumber(shift), sowId: selectedMainline.id }
                });
            },
        });
    };

    const handleJointChange = (jointId, field, value) => {
        setMainlineJoints((prev) =>
            prev.map((joint) => (joint.id === jointId ? { ...joint, [field]: value } : joint))
        );
        setIsDirty(true);
    };

    const handleDeletePipeString = (pipeStringId) => {
        if (!pipeStringId) {
            notification.error({ message: 'Cannot delete pipe string without valid ID.' });
            return;
        }
        // Show delete modal instead of confirm dialog
        showModal('deletePipeData', { id: pipeStringId });
    };

    const handleSkipChange = (e) => {
        const newSkipState = e.target.checked;
        setSkipped(newSkipState);
        setIsDirty(true);

        // Use the parent component's centralized skip handler
        if (onSkipChange) {
            onSkipChange(e);
        } else {
            // Fallback to local handling if no parent handler provided
            if (currentDsrLog?.id) {
                const submissionStatus = generateSubmissionStatus('production', shift, true);
                const sectionsSkipped = {
                    ...currentDsrLog.sectionsSkipped,
                    isProductionJointSkippedDayShift: shift === 'day' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedDayShift || 0),
                    isProductionJointSkippedNightShift: shift === 'night' ? (newSkipState ? 1 : 0) : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedNightShift || 0),
                };

                onUpdateDsrLogSections({
                    dsrLogId: currentDsrLog.id,
                    submissionStatus: submissionStatus,
                    sectionsSkipped: sectionsSkipped
                });
            }
        }
    };

    // Helper function to generate submission status fields for a specific section and shift
    const generateSubmissionStatus = (section, shift, isSubmitted = true) => {
        const shiftKeySuffix = shift === 'day' ? 'Dayshift' : 'Nightshift';
        let submittedKey;

        switch (section) {
            case 'technician':
                submittedKey = `isTechnicianLogSubmitted${shiftKeySuffix}`;
                break;
            case 'downtime':
                submittedKey = `isDowntimeSubmitted${shiftKeySuffix}`;
                break;
            case 'consumables':
                submittedKey = `isConsumablesSubmitted${shiftKeySuffix}`;
                break;
            case 'equipment':
                submittedKey = `isEquipmentUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'ehs':
                submittedKey = `isEhsIssuesSubmitted${shiftKeySuffix}`;
                break;
            case 'material':
                submittedKey = `isMaterialUsageSubmitted${shiftKeySuffix}`;
                break;
            case 'production':
                submittedKey = `isProductionJointSubmitted${shiftKeySuffix}`;
                break;
            default:
                return {};
        }

        return { [submittedKey]: isSubmitted };
    };

    const buildSavePayload = () => {
        if (!selectedMainline?.id || !selectedProject?.id) return null;

        const dsrLogProductionJoint = [];
        mainlineJoints.forEach((joint) => {
            if (joint.id) {
                dsrLogProductionJoint.push({
                    productionJointId: joint.id,
                    firstJointValue: joint.firstJointValue || 0,
                    lastJointValue: joint.lastJointValue || 0,
                    repair: joint.repair || 0,
                    accepted: joint.accepted || 0,
                    rejected: joint.rejected || 0,
                    shift: joint.shift ?? getShiftNumber(shift),
                    dsrLogId: joint.dsrLogId || null,
                });
            }
        });

        const logEntries = Array.isArray(currentProductionLog)
            ? currentProductionLog
            : currentProductionLog
              ? [currentProductionLog]
              : [];
        const firstEntry = logEntries[0] || {};
        const dayShiftComments = shift === 'day' ? comments : (firstEntry.dayShiftComments || '');
        const nightShiftComments = shift === 'night' ? comments : (firstEntry.nightShiftComments || '');

        return {
            skipped: false,
            sowId: selectedMainline.id,
            apiPayload: {
                dsrLogProductionJoint,
                dayShiftComments,
                nightShiftComments,
                projectId: selectedProject.id,
                logDate: moment(selectedDate).format('YYYY-MM-DD'),
                userEmail: JSON.parse(localStorage.getItem('user')).email,
                shift: getShiftNumber(shift),
                sowId: selectedMainline.id,
            },
        };
    };

    const handleSave = async () => {
        if (skipped) {
            if (onComplete) onComplete(true, 'production');
            return;
        }

        const savePayload = buildSavePayload();
        if (!savePayload) return;

        await onUpdateProductionLog(savePayload.apiPayload);
        setIsDirty(false);

        const submissionStatus = generateSubmissionStatus('production', shift, true);
        const sectionsSkipped = {
            ...currentDsrLog.sectionsSkipped,
            isProductionJointSkippedDayShift: shift === 'day' ? 0 : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedDayShift || 0),
            isProductionJointSkippedNightShift: shift === 'night' ? 0 : (currentDsrLog.sectionsSkipped?.isProductionJointSkippedNightShift || 0),
        };

        await onUpdateDsrLogSections({
            dsrLogId: currentDsrLog?.id,
            submissionStatus,
            sectionsSkipped,
        });

        if (onComplete) onComplete(true, 'production');

        setTimeout(() => {
            if (selectedProject && selectedMainline && selectedDate) {
                const userEmail = JSON.parse(localStorage.getItem('user')).email;
                onFetchProductionLog({
                    projectId: selectedProject.id,
                    logDate: moment(selectedDate).format('YYYY-MM-DD'),
                    userEmail,
                    shift: getShiftNumber(shift),
                    sowId: selectedMainline.id,
                });
            }
        }, 100);
    };

    const handleNext = () => {
        if (isDirty) {
            Modal.confirm({
                title: 'Warning!',
                icon: <ExclamationCircleOutlined />,
                content: 'You have made changes to the table. Do you want to save them?',
                okText: 'Save',
                cancelText: 'Discard',
                onOk: () => {
                    handleSave();
                    if (onComplete) onComplete(!skipped);
                },
                onCancel: () => {
                    if (onComplete) onComplete(!skipped);
                }
            });
        } else {
            if (onComplete) onComplete(!skipped);
        }
    };

    useEffect(() => {
        console.group('[DSR-PJ-Coating] DSR Production joints — mainline context');
        logMainlinePlannedCoatingDebug(selectedMainline, 'selectedMainline (DSR pick)');
        if (Array.isArray(sowDetails) && sowDetails.length) {
            console.log('[DSR-PJ-Coating] all project mainlines (Details data)', sowDetails.length);
            sowDetails.forEach((row, index) => {
                const section = row.pipelineSection ?? row.pipeline_section ?? `row-${index}`;
                logMainlinePlannedCoatingDebug(row, `sowDetails[${index}] ${section}`);
            });
        } else {
            console.warn('[DSR-PJ-Coating] sowDetails empty — cannot compare per mainline');
        }
        if (Array.isArray(currentProductionLog)) {
            flattenProductionLogToJoints(currentProductionLog, getShiftNumber(shift)).forEach(
                (joint, index) => {
                    const mainline = resolveMainlineForJoint(joint, sowDetails, selectedMainline);
                    console.log('[DSR-PJ-Coating] mainline row', {
                        rowIndex: index,
                        jointId: joint.id,
                        pipeDescription: joint.pipeDescription,
                        sowId: joint.sowId,
                        matchedMainlineId: mainline?.id,
                        matchedPipelineSection: mainline?.pipelineSection ?? mainline?.pipeline_section,
                        displayCoating: formatPlannedCoatingScopeDisplay(mainline),
                    });
                }
            );
        }
        console.groupEnd();
    }, [selectedMainline, sowDetails, currentProductionLog]);

    const getCoatingMainlineForJoint = (joint) =>
        resolveMainlineForJoint(joint, sowDetails, selectedMainline);

    const displayMainlines = useMemo(() => {
        const q = mainlineSearch.trim().toLowerCase();
        const entries = mainlineJoints.map((joint) => ({
            joint,
            mainlineName: getJointMainlineName(joint, sowDetails, selectedMainline),
        }));

        const sorted = [...entries].sort((a, b) =>
            compareMainlineNames(a.mainlineName, b.mainlineName)
        );

        if (!q) return sorted;
        return sorted.filter((entry) => entry.mainlineName.toLowerCase().includes(q));
    }, [mainlineJoints, mainlineSearch, sowDetails, selectedMainline]);

    return (
        <div className="modern-dsr-tech-log-section">
            {isShiftSkipped ? (
                <div className="modern-dsr-tech-log-skipped-banner">
                    <strong>
                        {shift.charAt(0).toUpperCase() + shift.slice(1)} Shift Skipped
                    </strong>
                    <p>
                        This section is disabled because the {shift} shift has been skipped.
                    </p>
                </div>
            ) : (
                <Spin
                    spinning={isProductionLogLoading}
                    indicator={(
                        <span className="modern-dsr-tech-log-spin" aria-hidden>
                            <Loader2 className="modern-dsr-tech-log-spin-icon" size={28} strokeWidth={2} />
                        </span>
                    )}
                    tip="Loading production joints data..."
                    size="large"
                >
                    <div className="modern-dsr-tech-log-card">
                        <div className="modern-dsr-tech-log-card-head">
                            <div className="modern-dsr-tech-log-card-title-block">
                                <div className="modern-dsr-tech-log-card-icon" aria-hidden>
                                    <Factory size={22} strokeWidth={2} />
                                </div>
                                <div>
                                    <h3 className="modern-dsr-tech-log-card-title">Production joints</h3>
                                    <p className="modern-dsr-tech-log-card-sub">Record first and last joint numbers, repairs, and accepted / rejected counts by pipe string.</p>
                                </div>
                            </div>
                        </div>

                        <div className="modern-dsr-pj-toolbar">
                            <div className="modern-dsr-pj-search">
                                <Search size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
                                <input
                                    value={mainlineSearch}
                                    onChange={(e) => setMainlineSearch(e.target.value)}
                                    placeholder="Search mainlines..."
                                    aria-label="Search mainlines"
                                />
                            </div>
                        </div>

                        <div className="modern-dsr-pj-list">
                            {displayMainlines.length === 0 ? (
                                <div className="modern-dsr-pj-empty-search">
                                    {mainlineSearch.trim()
                                        ? 'No mainlines match your search.'
                                        : 'No production joints for this shift.'}
                                </div>
                            ) : null}
                            {displayMainlines.map(({ joint }) => (
                              <NewPipeStringRow
                                key={joint.id}
                                pipeString={joint}
                                coatingMainline={getCoatingMainlineForJoint(joint)}
                                disabled={skipped}
                                onChange={handleJointChange}
                              />
                            ))}
                        </div>

                        <div className="modern-dsr-tech-log-issues-block">
                            <div className="modern-dsr-tech-log-issues-label">Comments</div>
                            <Input.TextArea
                                className="modern-dsr-tech-log-issues-textarea"
                                rows={4}
                                value={comments}
                                onChange={(e) => { setComments(e.target.value); setIsDirty(true); }}
                                disabled={skipped}
                            />
                        </div>
                    </div>
                </Spin>
            )}

            <div className="modern-dsr-tech-log-form-footer" aria-label="Form actions">
                <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
                <CrceButton
                    label={
                      isUpdatingProductionLog
                        ? 'Saving…'
                        : (isSubmitted ? 'Resubmit' : 'Save & Next')
                    }
                    variant="primary"
                    size="medium"
                    icon={Save}
                    onClick={handleSave}
                    disabled={isUpdatingProductionLog || isShiftSkipped}
                />
            </div>

            <ProductionJointsModal visible={isModalVisible} mode={modalConfig.mode} initialData={modalConfig.data} groupScope={modalConfig.context.groupScope} onCancel={handleCancel} onOk={handleOk} />
        </div>
    );
};

const mapStateToProps = ({ dsrReducer }) => ({
    currentDsrLog: dsrReducer.currentDsrLog,
    currentProductionLog: dsrReducer.currentProductionLog,
    isUpdatingProductionLog: dsrReducer.isUpdatingProductionLog,
    isProductionLogLoading: dsrReducer.isProductionLogLoading,
    isUpserting: dsrReducer.isUpsertingProductionItem,
    isDeleting: dsrReducer.isDeletingPipeGroup,
    isDeletingJoint: dsrReducer.isDeletingProductionJoint,
});

const mapDispatchToProps = {
    onFetchProductionLog: fetchProductionLog,
    onUpdateProductionLog: updateProductionLog,
    onUpsertProductionItem: upsertProductionItem,
    onDeletePipeGroup: deletePipeGroup,
    onUpdateDsrLogSections: updateDsrLogSections,
    onDeleteProductionJoint: deleteProductionJoint,
    onUpdatePipeGroup: updatePipeGroup,
    onUpdatePipeData: updatePipeData,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductionJointsSection); 